/**
 * ============================================================================
 * CORE SERVICE FACTORY: Universal Dynamic CRUD Service Generator
 * ============================================================================
 * Purpose:
 *   Generates standardized, type-safe API helper methods for any Master or
 *   Tenant REST endpoint. Handles pagination parameters, queries, creation,
 *   editing, and soft/hard deletion.
 * ============================================================================
 */

import api from "./api";

/**
 * Creates a reusable CRUD service for a specified endpoint slug.
 *
 * @param {string} resourceName - Endpoint path (e.g. "users", "master/users", "tenant/contacts")
 * @returns {Object} CRUD service containing get, view, create, edit, and delete methods
 */
export function createCrudService(resourceName) {
  const normalizedEndpoint = resourceName.startsWith("/") ? resourceName : `/${resourceName}`;

  return {
    /**
     * Retrieves paginated and filtered records.
     *
     * @param {Object} [params={}] - Query parameters { page, limit, search, sortBy, sortOrder }
     * @param {Object} [headers={}] - Custom headers (e.g. x-db-name, x-account-id)
     * @returns {Promise<Object>} API response payload
     */
    get: (params = {}, headers = {}) => {
      return api.get(normalizedEndpoint, { params, headers });
    },

    /**
     * Alias for get() to support getAll(params) patterns seamlessly.
     */
    getAll: (params = {}, headers = {}) => {
      return api.get(normalizedEndpoint, { params, headers });
    },

    /**
     * Views a single record by primary key or slug.
     *
     * @param {string|number} id - Record identifier
     * @param {Object} [headers={}] - Custom headers
     * @returns {Promise<Object>} API response payload
     */
    view: (id, headers = {}) => {
      return api.get(`${normalizedEndpoint}/${id}`, { headers });
    },

    /**
     * Alias for view() to support getById(id) patterns seamlessly.
     */
    getById: (id, headers = {}) => {
      return api.get(`${normalizedEndpoint}/${id}`, { headers });
    },

    /**
     * Creates a new record.
     *
     * @param {Object} data - Payload body
     * @param {Object} [headers={}] - Custom headers
     * @returns {Promise<Object>} API response payload
     */
    create: (data, headers = {}) => {
      return api.post(normalizedEndpoint, data, { headers });
    },

    /**
     * Updates an existing record by ID.
     *
     * @param {string|number} id - Record identifier
     * @param {Object} data - Updated fields
     * @param {Object} [headers={}] - Custom headers
     * @returns {Promise<Object>} API response payload
     */
    edit: (id, data, headers = {}) => {
      return api.put(`${normalizedEndpoint}/${id}`, data, { headers });
    },

    /**
     * Deletes a record (soft-delete by default, or hard-delete).
     *
     * @param {string|number} id - Record identifier
     * @param {boolean} [hard=false] - Whether to perform a hard delete
     * @param {Object} [headers={}] - Custom headers
     * @returns {Promise<Object>} API response payload
     */
    delete: (id, hard = false, headers = {}) => {
      return api.delete(`${normalizedEndpoint}/${id}`, { params: { hard }, headers });
    },
  };
}

export default createCrudService;
